import {
	BUDGET,
	MAX_PER_CLUB,
	POSITIONS,
	SQUAD_QUOTA,
	allPlayers,
	clubCounts,
	emptySquad,
	totalCost,
} from './draftRules.js';

/*
 * Fills a squad in one click.
 *
 * Selection is weighted by ownership rather than uniform. Uniform random
 * is easier — it averages £77.6m and never breaches the budget — but it
 * hands you fifteen players nobody has heard of, which is not a useful
 * starting point. Weighting toward owned players produces a recognisable
 * team and, measured over 2,000 runs, blows the budget about 83% of the
 * time. Hence the repair pass below.
 *
 * Weighting uses the Efraimidis-Spirakis key, random^(1/weight): sorting
 * on it is a correct weighted sample without replacement, unlike sorting
 * on `random * weight`, which biases toward the heaviest items far more
 * strongly than their weights warrant.
 */

/* Ownership is a percentage and often 0, so the floor keeps unowned
 * players reachable — a squad of only the top twenty picks would be the
 * same squad every time. */
const OWNERSHIP_FLOOR = 0.5;

const weightedKey = (player) =>
	Math.random() ** (1 / (player.tsb + OWNERSHIP_FLOOR));

function draw(players, { weighted }) {
	const byPosition = {};
	for (const position of POSITIONS) byPosition[position] = [];
	for (const player of players) byPosition[player.position]?.push(player);

	const squad = emptySquad();
	const clubs = {};

	for (const position of POSITIONS) {
		const ordered = byPosition[position]
			.map((player) => ({
				player,
				key: weighted ? weightedKey(player) : Math.random(),
			}))
			.sort((a, b) => b.key - a.key);

		let filled = 0;
		for (const { player } of ordered) {
			if (filled === SQUAD_QUOTA[position]) break;
			if ((clubs[player.team_name] || 0) >= MAX_PER_CLUB) continue;
			clubs[player.team_name] = (clubs[player.team_name] || 0) + 1;
			squad[position][filled] = player;
			filled += 1;
		}
		/* Not enough players at this position to fill the line legally. */
		if (filled < SQUAD_QUOTA[position]) return null;
	}

	return squad;
}

/*
 * Swap expensive players out until the squad is affordable.
 *
 * Each pass prefers a swap that clears the overspend outright and costs
 * the least ownership; failing that it takes the biggest saving available,
 * so the total strictly falls and the loop always terminates — the
 * cheapest legal squad is around £59m, well inside the budget.
 */
function repair(squad, players, maxPasses = 60) {
	const byPosition = {};
	for (const position of POSITIONS) byPosition[position] = [];
	for (const player of players) byPosition[player.position]?.push(player);

	for (let pass = 0; pass < maxPasses; pass += 1) {
		const spent = totalCost(squad);
		const excess = spent - BUDGET;
		if (excess <= 0) return squad;

		const picked = new Set(allPlayers(squad).map((player) => player.element));
		const clubs = clubCounts(squad);
		let best = null;

		for (const position of POSITIONS) {
			squad[position].forEach((current, index) => {
				if (!current) return;
				for (const candidate of byPosition[position]) {
					if (candidate.cost >= current.cost) continue;
					if (picked.has(candidate.element)) continue;
					/* The outgoing player frees a slot at their own club. */
					const held =
						(clubs[candidate.team_name] || 0) -
						(candidate.team_name === current.team_name ? 1 : 0);
					if (held >= MAX_PER_CLUB) continue;

					const saving = current.cost - candidate.cost;
					const loss = current.tsb - candidate.tsb;
					const clears = saving >= excess;
					/* Sufficient swaps beat insufficient ones; among equals,
					 * keep as much ownership as possible. */
					const better =
						!best ||
						(clears && !best.clears) ||
						(clears === best.clears &&
							(clears ? loss < best.loss : saving > best.saving));
					if (better) best = { position, index, candidate, saving, loss, clears };
				}
			});
		}

		if (!best) return null;
		squad[best.position][best.index] = best.candidate;
	}

	return totalCost(squad) <= BUDGET ? squad : null;
}

/*
 * A complete, legal, affordable squad — or null if one could not be built,
 * which the caller should treat as "leave the squad alone".
 */
export function autoPickSquad(players, { attempts = 8 } = {}) {
	for (let attempt = 0; attempt < attempts; attempt += 1) {
		const squad = draw(players, { weighted: true });
		if (squad && repair(squad, players)) return squad;
	}

	/* Weighted selection kept overshooting. Uniform random averages well
	 * under the budget, so it is the fallback rather than giving up. */
	for (let attempt = 0; attempt < attempts; attempt += 1) {
		const squad = draw(players, { weighted: false });
		if (squad && repair(squad, players)) return squad;
	}

	return null;
}
